import { useState } from 'react';
import { ActivityIndicator, Pressable, Text, View } from 'react-native';
import { Award, Check, ClipboardList, Flame, Flag, Target, Trophy, CircleDot } from 'lucide-react-native';
import { StyleSheet } from 'react-native-unistyles';
import { getAchievementHighlightPriority, type AchievementBadgeKey, type AchievementEvidence } from '@shared/achievements';

import { colors, typography } from '@/lib/theme';

export type BadgeKey = AchievementBadgeKey;
export type BadgeDefinition = { key: BadgeKey; title: string; description: string; rule: string; icon: string; repeatable: boolean; displayOrder?: number; ruleVersion?: number };
export type Earned = {
  id: string;
  badgeKey: BadgeKey;
  occurrenceKey: string;
  awardedAt: Date | string | null;
  isBackfilled: boolean;
  evidence: AchievementEvidence;
};
type Progress = { badgeKey: BadgeKey; current: number; target: number; unit: string; weekLabel?: string };
const progressText = (progress: Progress) => progress.unit === '%'
  ? `${progress.current}% · goal ${progress.target}%`
  : `${progress.current}/${progress.target} ${progress.unit}`;
export type AchievementCollectionData = {
  supported: boolean;
  catalog: BadgeDefinition[];
  earned: Earned[];
  progress: Progress[];
  counts: Partial<Record<BadgeKey, number>>;
};

const iconByName = {
  flag: Flag,
  clipboard: ClipboardList,
  target: Target,
  bullseye: CircleDot,
  trophy: Trophy,
  flame: Flame,
  check: Check,
} as const;

export function AchievementBadge({
  badge,
  earned,
  count = 0,
  compact = false,
}: {
  badge: BadgeDefinition;
  earned?: Earned;
  count?: number;
  compact?: boolean;
}) {
  const Icon = iconByName[badge.icon as keyof typeof iconByName] ?? Award;
  const evidence = earned?.evidence;
  const evidenceText = evidence
    ? [
        evidence.weekLabel,
        typeof evidence.correctCount === 'number' && typeof evidence.gradedCount === 'number'
          ? `${evidence.correctCount} of ${evidence.gradedCount} correct`
          : null,
        typeof evidence.weeklyPoints === 'number' ? `${evidence.weeklyPoints.toFixed(1)} points` : null,
        evidence.qualifyingWeeks?.length ? `${evidence.qualifyingWeeks.length} week streak` : null,
        typeof evidence.count === 'number' && evidence.unit ? `${evidence.count} ${evidence.unit}` : null,
      ].filter(Boolean).join(' · ')
    : '';

  return (
    <View style={[styles.badge, earned ? styles.badgeEarned : styles.badgeLocked, compact && styles.badgeCompact]}>
      <View style={[styles.iconShell, earned ? styles.iconShellEarned : styles.iconShellLocked]}>
        <Icon size={compact ? 17 : 20} color={earned ? colors.ink.field : colors.text.secondary} />
      </View>
      <View style={styles.badgeCopy}>
        <View style={styles.badgeTitleRow}>
          <Text style={styles.badgeTitle}>{badge.title}{count > 1 ? ` ×${count}` : ''}</Text>
          {earned?.awardedAt && !earned.isBackfilled ? (
            <Text style={styles.awardedAt}>{new Date(earned.awardedAt).toLocaleDateString()}</Text>
          ) : null}
        </View>
        <Text style={styles.badgeDescription}>{earned ? evidenceText || badge.description : badge.rule}</Text>
      </View>
    </View>
  );
}

export function AchievementCollection({
  data,
  isLoading,
  error,
  playerName,
}: {
  data?: AchievementCollectionData;
  isLoading: boolean;
  error?: boolean;
  playerName: string;
}) {
  const [expanded, setExpanded] = useState(false);
  if (isLoading) {
    return <View style={styles.panel}><ActivityIndicator color={colors.ink.field} /><Text style={styles.panelMessage}>Loading achievements…</Text></View>;
  }
  if (error) return <View style={styles.panel}><Text style={styles.panelTitle}>Achievements</Text><Text style={styles.panelMessage}>Achievements could not be loaded.</Text></View>;
  if (!data || !data.supported) return <View style={styles.panel}><Text style={styles.panelTitle}>Achievements</Text><Text style={styles.panelMessage}>Achievements are unavailable for this season.</Text></View>;

  const earnedByKey = new Map<BadgeKey, Earned[]>();
  data.earned.forEach((entry) => {
    const instances = earnedByKey.get(entry.badgeKey) ?? [];
    instances.push(entry);
    earnedByKey.set(entry.badgeKey, instances);
  });
  const earnedCatalog = data.catalog.filter((badge) => earnedByKey.has(badge.key))
    .sort((a, b) => getAchievementHighlightPriority(a.key) - getAchievementHighlightPriority(b.key));
  const preview = earnedCatalog.slice(0, 3);
  const progressByKey = new Map(data.progress.map((item) => [item.badgeKey, item]));

  return (
    <View style={styles.panel}>
      <View style={styles.panelHeader}>
        <View>
          <Text style={styles.eyebrow}>{playerName === 'Your' ? 'SEASON COLLECTION' : 'PLAYER RECORD'}</Text>
          <Text style={styles.panelTitle}>{playerName === 'Your' ? 'Your badge case' : `${playerName}’s achievements`}</Text>
        </View>
        <View style={styles.countPill}><Text style={styles.countText}>{data.earned.length} earned</Text></View>
      </View>

      {!expanded ? preview.length ? (
        <View style={styles.badgeList}>
          {preview.map((badge) => (
            <AchievementBadge key={badge.key} badge={badge} earned={earnedByKey.get(badge.key)?.at(-1)} count={data.counts[badge.key] ?? 0} compact />
          ))}
        </View>
      ) : (
        <Text style={styles.panelMessage}>No badges yet. The first one starts with a saved pick.</Text>
      ) : null}

      {data.progress.length > 0 && !expanded ? (
        <View style={styles.progressPreview}>
          {data.progress.slice(0, 2).map((item) => (
            <Text key={item.badgeKey} style={styles.progressText}>
              {data.catalog.find((badge) => badge.key === item.badgeKey)?.title}: {progressText(item)}
            </Text>
          ))}
        </View>
      ) : null}

      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        onPress={() => setExpanded((value) => !value)}
        style={styles.viewAllButton}
      >
        <Text style={styles.viewAllText}>{expanded ? 'Show less' : 'View all'}</Text>
        <Text style={styles.viewAllText}>{expanded ? '↑' : '→'}</Text>
      </Pressable>

      {expanded ? (
        <View style={styles.fullCollection}>
          {data.catalog.map((badge) => {
            const instances = earnedByKey.get(badge.key) ?? [];
            const progress = progressByKey.get(badge.key);
            return (
              <View key={badge.key} style={styles.fullBadgeGroup}>
                {instances.length ? instances.map((earned, index) => (
                  <AchievementBadge key={`${badge.key}-${earned.occurrenceKey}-${index}`} badge={badge} earned={earned} />
                )) : <AchievementBadge badge={badge} />}
                {!instances.length && progress ? <Text style={styles.progressText}>Progress: {progressText(progress)}{progress.weekLabel ? ` · ${progress.weekLabel}` : ''}</Text> : null}
              </View>
            );
          })}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create(() => ({
  awardedAt: { color: colors.text.secondary, fontFamily: typography.fontFamily.mono, fontSize: 9 },
  badge: { alignItems: 'center', borderRadius: 8, borderWidth: 1, flexDirection: 'row', gap: 10, minHeight: 66, padding: 10 },
  badgeCompact: { minHeight: 54 },
  badgeCopy: { flex: 1, gap: 3 },
  badgeDescription: { color: colors.text.secondary, fontFamily: typography.fontFamily.body, fontSize: 11, lineHeight: 15 },
  badgeEarned: { backgroundColor: colors.success[50], borderColor: colors.cardBorder },
  badgeList: { gap: 8 },
  badgeLocked: { backgroundColor: colors.ink.paper, borderColor: colors.cardBorder },
  badgeTitle: { color: colors.text.primary, fontFamily: typography.fontFamily.bodyMedium, fontSize: 13, fontWeight: '700' },
  badgeTitleRow: { alignItems: 'center', flexDirection: 'row', flexWrap: 'wrap', gap: 8, justifyContent: 'space-between' },
  countPill: { backgroundColor: colors.ink.field, borderRadius: 99, paddingHorizontal: 10, paddingVertical: 6 },
  countText: { color: colors.ink.paper, fontFamily: typography.fontFamily.mono, fontSize: 10, fontWeight: '700' },
  eyebrow: { color: colors.text.secondary, fontFamily: typography.fontFamily.mono, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' },
  fullBadgeGroup: { gap: 5 },
  fullCollection: { gap: 10 },
  iconShell: { alignItems: 'center', borderRadius: 8, height: 38, justifyContent: 'center', width: 38 },
  iconShellEarned: { backgroundColor: colors.ink.paperDark },
  iconShellLocked: { backgroundColor: colors.ink.paperDark },
  panel: { backgroundColor: colors.ink.paper, borderColor: colors.cardBorder, borderRadius: 10, borderWidth: 1, gap: 12, padding: 16 },
  panelHeader: { alignItems: 'center', flexDirection: 'row', gap: 12, justifyContent: 'space-between' },
  panelMessage: { color: colors.text.secondary, fontFamily: typography.fontFamily.body, fontSize: 13, lineHeight: 18 },
  panelTitle: { color: colors.text.primary, fontFamily: typography.fontFamily.display, fontSize: 18, fontWeight: '700', marginTop: 3 },
  progressPreview: { borderTopColor: colors.cardBorder, borderTopWidth: 1, gap: 5, paddingTop: 10 },
  progressText: { color: colors.text.secondary, fontFamily: typography.fontFamily.mono, fontSize: 10, lineHeight: 15 },
  viewAllButton: { alignItems: 'center', borderTopColor: colors.cardBorder, borderTopWidth: 1, flexDirection: 'row', justifyContent: 'space-between', paddingTop: 10 },
  viewAllText: { color: colors.ink.field, fontFamily: typography.fontFamily.bodyMedium, fontSize: 12, fontWeight: '700' },
}));
